import { WrappedColumnHeader } from "components/common/dataGrid/WrappedColumnHeader";
import UndoIcon from "@mui/icons-material/Undo";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import { AlertColor } from "@mui/material";
import { GridActionsCellItem, GridColDef } from "@mui/x-data-grid-premium";
import { useMemo } from "react";
import {
  MaterialOverrideRow,
  ScrapChefChemistryComparison,
  ChemistryRowWarnings,
} from "./types";
import { SearchMaterialChemistry } from "contexts/search/context";
import {
  parseChemistryMeasurement,
  chemistryMeasurementsEqual,
  chemistryMeasurementKey,
} from "./chemistryMeasurements";
import { useFormatNumber } from "src/utils/formatNumber";
import { SupportedLanguage } from "store/api/generatedApi";
import { useLanguage } from "hooks/settings";
import { ChemistryMeasurementCell } from "./ChemistryMeasurementCell";

export interface UseMaterialOverrideColumnsArgs {
  t: (key: string) => string;
  notifyStatus: (args: { text: string; type: AlertColor }) => void;
  materialChemistry: SearchMaterialChemistry;
  handleDeleteItem: (row: MaterialOverrideRow) => void;
  showScale: boolean;
  scrapchefComparison?: ScrapChefChemistryComparison;
  rowWarnings?: ChemistryRowWarnings;
}

export const useMaterialOverrideColumns = ({
  t,
  notifyStatus,
  materialChemistry,
  handleDeleteItem,
  showScale,
  scrapchefComparison,
  rowWarnings,
}: UseMaterialOverrideColumnsArgs): GridColDef<MaterialOverrideRow>[] => {
  const language = useLanguage();
  const formatNumber = useFormatNumber();
  return useMemo(() => {
    const fields: ("loc" | "scale")[] = showScale ? ["loc", "scale"] : ["loc"];
    return [
      {
        field: "material_name",
        headerName: t("material"),
        sortable: true,
        filterable: true,
        flex: 1,
        editable: false,
        display: "flex",
      },
      {
        field: "chemical_element_name",
        headerName: t("element"),
        sortable: true,
        filterable: false,
        flex: 1,
        editable: false,
        display: "flex",
      },
      ...fields.map((field): GridColDef<MaterialOverrideRow> => ({
        field,
        headerName: field === "loc" && !showScale ? t("value") : t(field),
        sortable: true,
        filterable: false,
        editable: true,
        flex: 1,
        cellClassName: ({ row }) =>
          `numeric ${(field === "loc" ? row.is_loc_overridden : row.is_scale_overridden) ? "overridden-cell" : ""}`,
        valueSetter: (text: string | undefined, row) => {
          try {
            const measurement = parseChemistryMeasurement({
              text: text ?? "",
              language: language as SupportedLanguage,
            });
            return { ...row, [field]: measurement, is_overridden: true };
          } catch {
            notifyStatus({ text: t("invalidNumber"), type: "error" });
            return row;
          }
        },
        valueGetter: (_: unknown, row) => formatNumber(4)(row[field]),
        renderCell: ({ row }) => (
          <ChemistryMeasurementCell
            row={row}
            field={field}
            materialChemistry={materialChemistry}
            scrapchefChemistry={scrapchefComparison?.measurements}
            warning={rowWarnings?.get(chemistryMeasurementKey(row))}
          />
        ),
        display: "flex",
      })),
      {
        field: "discard",
        type: "actions",
        headerName: t(scrapchefComparison ? "discardUnsavedChanges" : "revert"),
        renderHeader: scrapchefComparison
          ? () => <WrappedColumnHeader label={t("discardUnsavedChanges")} />
          : undefined,
        width: scrapchefComparison ? 220 : 100,
        filterable: false,
        getActions: ({ row }) =>
          row.is_overridden
            ? [
                <GridActionsCellItem
                  key="discard"
                  icon={<UndoIcon />}
                  label={t(
                    scrapchefComparison
                      ? "discardUnsavedChanges"
                      : "discardChanges"
                  )}
                  onClick={() => {
                    handleDeleteItem(row);
                  }}
                  color="inherit"
                />,
              ]
            : [],
        display: "flex",
      },
      ...(scrapchefComparison
        ? [
            {
              field: "restore",
              type: "actions" as const,
              headerName: t("restoreScrapchefDefaults"),
              renderHeader: () => (
                <WrappedColumnHeader label={t("restoreScrapchefDefaults")} />
              ),
              width: 220,
              filterable: false,
              getActions: ({ row }: { row: MaterialOverrideRow }) => {
                const scrapchef =
                  scrapchefComparison.measurements?.[row.material_id]?.[
                    row.chemical_element_id
                  ];
                if (
                  scrapchef === undefined ||
                  chemistryMeasurementsEqual(scrapchef, row)
                )
                  return [];
                return [
                  <GridActionsCellItem
                    key="scrapchef"
                    icon={<RestartAltIcon />}
                    label={t("restoreScrapchefDefaults")}
                    onClick={() => {
                      scrapchefComparison.onRevert(scrapchef);
                    }}
                    color="inherit"
                  />,
                ];
              },
              display: "flex" as const,
            },
          ]
        : []),
    ];
  }, [
    t,
    language,
    formatNumber,
    notifyStatus,
    materialChemistry,
    handleDeleteItem,
    showScale,
    scrapchefComparison,
    rowWarnings,
  ]);
};
